import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { t } from '@/i18n';
import { useCompaniesApi, useRegistryApi, DuplicateNipError } from '@/services/api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface AddCompanyDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

interface FormValues {
  nip: string;
  name: string;
  street: string;
  postalCode: string;
  city: string;
  country: string;
}

const EMPTY_FORM: FormValues = {
  nip: '',
  name: '',
  street: '',
  postalCode: '',
  city: '',
  country: 'PL',
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-muted-foreground text-xs">{label}</Label>
      {children}
    </div>
  );
}

export function AddCompanyDialog({ open, onClose, onCreated }: AddCompanyDialogProps) {
  const [values, setValues] = useState<FormValues>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [fetchingBir, setFetchingBir] = useState(false);
  const { createCompany } = useCompaniesApi();
  const { lookupRegistry } = useRegistryApi();

  const setField = <K extends keyof FormValues>(field: K, value: FormValues[K]) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const nipDigits = values.nip.replace(/[\s-]/g, '');
  const canFetchBir = /^\d{10}$/.test(nipDigits);

  const resetAndClose = () => {
    setValues(EMPTY_FORM);
    onClose();
  };

  const handleFetchFromBir = async () => {
    try {
      setFetchingBir(true);
      const data = await lookupRegistry(nipDigits);
      setValues((prev) => ({
        ...prev,
        name: data.name || prev.name,
        street: data.street || prev.street,
        postalCode: data.postalCode || prev.postalCode,
        city: data.city || prev.city,
        country: data.country || prev.country || 'PL',
      }));
      toast.success(t('registry.lookupSuccess'));
    } catch (error) {
      toast.error(t('registry.lookupFailed'));
      console.error(error);
    } finally {
      setFetchingBir(false);
    }
  };

  const handleSubmit = async () => {
    if (!values.nip.trim()) return;
    try {
      setSaving(true);
      await createCompany({
        nip: nipDigits,
        name: values.name || undefined,
        street: values.street || undefined,
        postalCode: values.postalCode || undefined,
        city: values.city || undefined,
        country: values.country || undefined,
      });
      toast.success(t('companies.createSuccess'));
      resetAndClose();
      onCreated();
    } catch (error) {
      if (error instanceof DuplicateNipError) {
        toast.error(t('companies.duplicateNip'));
      } else {
        toast.error(error instanceof Error ? error.message : t('errors.saveFailed'));
      }
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && resetAndClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('companies.addCompany')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Field label={t('companies.nip')}>
                <Input
                  value={values.nip}
                  onChange={(e) => setField('nip', e.target.value)}
                  placeholder="0000000000"
                  maxLength={12}
                />
              </Field>
            </div>
            <Button
              type="button"
              variant="outline"
              disabled={!canFetchBir || fetchingBir}
              onClick={handleFetchFromBir}
            >
              {fetchingBir && <Loader2 className="size-4 animate-spin" />}
              {t('registry.fetch')}
            </Button>
          </div>

          <Field label={t('companies.name')}>
            <Input value={values.name} onChange={(e) => setField('name', e.target.value)} />
          </Field>

          <Field label={t('companies.street')}>
            <Input value={values.street} onChange={(e) => setField('street', e.target.value)} />
          </Field>

          <div className="grid grid-cols-[1fr_1.25fr_0.75fr] gap-2">
            <Field label={t('companies.postalCode')}>
              <Input
                value={values.postalCode}
                onChange={(e) => setField('postalCode', e.target.value)}
                placeholder="00-000"
              />
            </Field>
            <Field label={t('companies.city')}>
              <Input value={values.city} onChange={(e) => setField('city', e.target.value)} />
            </Field>
            <Field label={t('companies.country')}>
              <Input
                value={values.country}
                onChange={(e) => setField('country', e.target.value)}
                placeholder="PL"
              />
            </Field>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={resetAndClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={saving || !values.nip.trim()}>
            {saving && <Loader2 className="size-4 animate-spin" />}
            {t('common.add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
